import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

const NAIVE_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/;
export const markNaiveTimestampsAsUTC = (value) => {
  if (typeof value === 'string') return NAIVE_ISO_RE.test(value) ? `${value}Z` : value;
  if (Array.isArray(value)) return value.map(markNaiveTimestampsAsUTC);
  if (value && typeof value === 'object') {
    for (const k of Object.keys(value)) value[k] = markNaiveTimestampsAsUTC(value[k]);
  }
  return value;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Backend emits UTC timestamps without a zone suffix ("2026-10-12T05:49:41.124000").
    // Browsers parse those as LOCAL time, which shifts every displayed time by the user's
    // UTC offset. Mark them as UTC once here so every formatter renders correct IST.
    if (response?.data && typeof response.data === 'object') {
      response.data = markNaiveTimestampsAsUTC(response.data);
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired, try to refresh
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken && !error.config._retry) {
        error.config._retry = true;
        try {
          const response = await axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken
          });
          const { access_token, refresh_token: newRefresh } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefresh);
          error.config.headers.Authorization = `Bearer ${access_token}`;
          return api(error.config);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH API ====================
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  refresh: (data) => api.post('/auth/refresh', data)
};

// ==================== USERS API ====================
export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`)
};

// ==================== ROLES API ====================
export const rolesAPI = {
  list: (params) => api.get('/roles', { params }),
  get: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  initDefaults: () => api.post('/roles/init-defaults')
};

// ==================== WAREHOUSES API ====================
export const warehousesAPI = {
  list: (params) => api.get('/warehouses', { params }),
  get: (id) => api.get(`/warehouses/${id}`),
  create: (data) => api.post('/warehouses', data),
  update: (id, data) => api.put(`/warehouses/${id}`, data),
  delete: (id) => api.delete(`/warehouses/${id}`),
  getDropdown: () => api.get('/warehouses/dropdown').then(res => res.data)
};

// ==================== STORES API ====================
export const storesAPI = {
  list: (params) => api.get('/stores', { params }),
  get: (id) => api.get(`/stores/${id}`),
  getByCode: (code) => api.get(`/stores/code/${code}`),
  getDropdown: () => api.get('/stores/dropdown').then(res => res.data),
  create: (data) => api.post('/stores', data),
  update: (id, data) => api.put(`/stores/${id}`, data),
  delete: (id) => api.delete(`/stores/${id}`),
  addCounter: (id, data) => api.post(`/stores/${id}/counters`, data),
  addShift: (id, data) => api.post(`/stores/${id}/shifts`, data),
  getNextBillNumber: (id) => api.get(`/stores/${id}/next-bill-number`),
  getDiscountSettings: (id) => api.get(`/stores/${id}/discount-settings`),
  getGstInvoiceSettings: (id) => api.get(`/stores/${id}/gst-invoice-settings`),
  updateGstInvoiceSettings: (id, data) => api.put(`/stores/${id}/gst-invoice-settings`, data),
  uploadBranchPhoto: (id, fd) => api.post(`/hq/stores/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteBranchPhoto: (id, url) => api.delete(`/hq/stores/${id}/photo`, { params: { url } }),
};

// ==================== BRANDS API ====================
export const brandsAPI = {
  list: (params) => api.get('/brands', { params }),
  get: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`),
  // Auto-generated code
  getNextCode: () => api.get('/brands/next-code').then(res => res.data)
};

// ==================== CATEGORIES API ====================
export const categoriesAPI = {
  list: (params) => api.get('/categories', { params }),
  tree: () => api.get('/categories/tree'),
  get: (id) => api.get(`/categories/${id}`),
  getChildren: (id) => api.get(`/categories/${id}/children`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  // Auto-generated code
  getNextCode: () => api.get('/categories/next-code').then(res => res.data)
};

// ==================== UOM API ====================
export const uomAPI = {
  list: (params) => api.get('/uom', { params }),
  get: (id) => api.get(`/uom/${id}`),
  create: (data) => api.post('/uom', data),
  update: (id, data) => api.put(`/uom/${id}`, data),
  delete: (id) => api.delete(`/uom/${id}`),
  initDefaults: () => api.post('/uom/init-defaults')
};

// ==================== TAX API ====================
export const taxAPI = {
  list: (params) => api.get('/tax', { params }),
  get: (id) => api.get(`/tax/${id}`),
  create: (data) => api.post('/tax', data),
  update: (id, data) => api.put(`/tax/${id}`, data),
  delete: (id) => api.delete(`/tax/${id}`),
  initDefaults: () => api.post('/tax/init-defaults'),
  // HSN Mappings
  listHSN: (params) => api.get('/tax/hsn-mapping', { params }),
  getHSN: (code) => api.get(`/tax/hsn-mapping/${code}`),
  createHSN: (params) => api.post('/tax/hsn-mapping', null, { params })
};

// ==================== PRODUCTS API ====================
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  getByBarcode: (barcode) => api.get(`/products/barcode/${barcode}`),
  barcodesLookup: (product_ids) => api.post('/products/barcodes/lookup', { product_ids }),
  search: (q, limit = 20) => api.get('/products/search', { params: { q, limit } }),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  reactivate: (id) => api.post(`/products/${id}/reactivate`),
  // Pricing
  setPrice: (id, data) => api.post(`/products/${id}/price`, data),
  getPrice: (id, variantId = null) => api.get(`/products/${id}/price`, { params: { variant_id: variantId } }),
  getPriceHistory: (id, variantId = null) => api.get(`/products/${id}/price-history`, { params: { variant_id: variantId } }),
  // Supplier & Low Stock
  getBySupplier: (supplierId, search = null) => api.get(`/products/by-supplier/${supplierId}`, { params: { search } }),
  getLowStock: (supplierId = null) => api.get('/products/low-stock/list', { params: { supplier_id: supplierId } }),
  // Last Purchase Rate
  getLastPurchaseRate: (id) => api.get(`/products/${id}/last-purchase-rate`),
  getBatchLastPurchaseRates: (productIds) => api.post('/products/batch/last-purchase-rates', productIds),
  // Auto-generated code
  getNextSku: () => api.get('/products/next-sku').then(res => res.data)
};

// ==================== VENDORS API ====================
export const vendorsAPI = {
  list: (params) => api.get('/vendors', { params }),
  get: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  getLedger: (id) => api.get(`/vendors/${id}/ledger`),
  getProducts: (id, params) => api.get(`/vendors/${id}/products`, { params }),
  dropdown: () => api.get('/vendors/dropdown'),
  // Franchise direct purchase vendors
  getFranchiseDirectVendors: () => api.get('/vendors/franchise-direct/available'),
  // Auto-generated code
  getNextCode: () => api.get('/vendors/next-code').then(res => res.data)
};

// ==================== OFFERS API ====================
export const offersAPI = {
  list: (params) => api.get('/offers', { params }),
  get: (id) => api.get(`/offers/${id}`),
  getActive: (storeId) => api.get('/offers/active', { params: { store_id: storeId } }),
  getAlerts: () => api.get('/offers/alerts'),
  getForProduct: (productId, storeId, qty = 1, unitPrice = null, mrp = null) =>
    api.get('/offers/for-product', { params: { product_id: productId, store_id: storeId, quantity: qty, unit_price: unitPrice, mrp } }),
  validateCoupon: (couponCode, storeId, customerPhone = null) => 
    api.get('/offers/validate-coupon', { params: { coupon_code: couponCode, store_id: storeId, customer_phone: customerPhone } }),
  create: (data) => api.post('/offers', data),
  update: (id, data) => api.put(`/offers/${id}`, data),
  delete: (id) => api.delete(`/offers/${id}`)
};

// ==================== LOCATIONS API ====================
export const locationsAPI = {
  getCountries: () => api.get('/locations/countries'),
  getStates: (country = 'IN') => api.get('/locations/states', { params: { country } }),
  getStatesDropdown: () => api.get('/locations/states').then(res => res.data),
  getDistricts: (stateId) => api.get('/locations/districts/dropdown', { params: { state_id: stateId } }),
  getAllDistricts: () => api.get('/locations/districts-list'),
  search: (q, limit = 10) => api.get('/locations/search', { params: { q, limit } }),
  getByPincode: (pincode) => api.get(`/locations/by-pincode/${pincode}`),
  searchPincodes: (search, limit = 50) => api.get('/locations/pincodes', { params: { search, page_size: limit } }).then(res => res.data?.items || [])
};

// ==================== PURCHASE ORDERS API ====================
export const purchaseOrdersAPI = {
  list: (params) => api.get('/purchase-orders', { params }),
  get: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  submit: (id) => api.post(`/purchase-orders/${id}/submit`),
  approve: (id) => api.post(`/purchase-orders/${id}/approve`),
  reject: (id, reason) => api.post(`/purchase-orders/${id}/reject`, null, { params: { reason } }),
  cancel: (id, reason) => api.post(`/purchase-orders/${id}/cancel`, null, { params: { reason } })
};

// ==================== GRN API ====================
export const grnAPI = {
  list: (params) => api.get('/grn', { params }),
  get: (id) => api.get(`/grn/${id}`),
  create: (data) => api.post('/grn', data),
  update: (id, data) => api.put(`/grn/${id}`, data),
  delete: (id) => api.delete(`/grn/${id}`),
  complete: (id) => api.post(`/grn/${id}/complete`),
  cancel: (id, reason) => api.post(`/grn/${id}/cancel`, null, { params: { reason } }),
  getPendingPOItems: (poId) => api.get(`/grn/po/${poId}/pending-items`),
  // Store GRN endpoints
  createStoreGRN: (data) => api.post('/grn/store', data),
  completeStoreGRN: (id) => api.post(`/grn/store/${id}/complete`)
};

// ==================== GRN V2 API ====================
export const grnV2API = {
  // Local GRN
  listLocal: (params) => api.get('/grn-v2/local', { params }),
  getLocal: (id) => api.get(`/grn-v2/local/${id}`),
  createLocal: (data) => api.post('/grn-v2/local', data),
  submitLocalForVerification: (id) => api.post(`/grn-v2/local/${id}/submit-for-verification`),
  verifyLocal: (id, data) => api.post(`/grn-v2/local/${id}/verify`, data || null),
  completeLocal: (id) => api.post(`/grn-v2/local/${id}/complete`),
  cancelLocal: (id, reason) => api.post(`/grn-v2/local/${id}/cancel`, null, { params: { reason } }),
  deleteLocal: (id) => api.delete(`/grn-v2/local/${id}`),
  updateLocal: (id, data) => api.put(`/grn-v2/local/${id}`, data),
  // International GRN
  listInternational: (params) => api.get('/grn-v2/international', { params }),
  getInternational: (id) => api.get(`/grn-v2/international/${id}`),
  createInternational: (data) => api.post('/grn-v2/international', data),
  submitInternationalForVerification: (id) => api.post(`/grn-v2/international/${id}/submit-for-verification`),
  verifyInternational: (id, data) => api.post(`/grn-v2/international/${id}/verify`, data || null),
  completeInternational: (id) => api.post(`/grn-v2/international/${id}/complete`),
  cancelInternational: (id, reason) => api.post(`/grn-v2/international/${id}/cancel`, null, { params: { reason } }),
  deleteInternational: (id) => api.delete(`/grn-v2/international/${id}`),
  updateInternational: (id, data) => api.put(`/grn-v2/international/${id}`, data),
  // Warehouse pending verification
  listPendingVerification: () => api.get('/grn-v2/warehouse/pending-verification'),
  // Combined list (fetch both and merge)
  listAll: async (params) => {
    const [local, international] = await Promise.all([
      api.get('/grn-v2/local', { params }),
      api.get('/grn-v2/international', { params })
    ]);
    const combined = [
      ...(local.data || []).map(g => ({ ...g, grn_type: 'local' })),
      ...(international.data || []).map(g => ({ ...g, grn_type: 'international' }))
    ];
    // Sort by created_at desc
    combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return { data: combined };
  }
};

// ==================== PURCHASE RETURNS API ====================
export const purchaseReturnsAPI = {
  list: (params) => api.get('/purchase-returns', { params }),
  get: (id) => api.get(`/purchase-returns/${id}`),
  create: (data) => api.post('/purchase-returns', data),
  update: (id, data) => api.put(`/purchase-returns/${id}`, data),
  approve: (id) => api.post(`/purchase-returns/${id}/approve`),
  post: (id) => api.post(`/purchase-returns/${id}/post`),
  cancel: (id, reason) => api.post(`/purchase-returns/${id}/cancel`, null, { params: { reason } }),
  pdfUrl: (id) => `${API_BASE}/purchase-returns/${id}/pdf`
};

// ==================== VENDOR PAYMENTS API ====================
export const vendorPaymentsAPI = {
  list: (params) => api.get('/vendor-payments', { params }),
  get: (id) => api.get(`/vendor-payments/${id}`),
  create: (data) => api.post('/vendor-payments', data),
  update: (id, data) => api.put(`/vendor-payments/${id}`, data),
  cancel: (id, reason, security_code) => api.post(`/ledgers/vendor/payments/${id}/void`, { reason, security_code }),
  getVendorLedger: (vendorId, params) => api.get(`/vendor-payments/vendor/${vendorId}/ledger`, { params }),
  ledgerSummary: (params) => api.get('/vendor-payments/ledger-summary', { params }),
  getOutstandingGRNs: (vendorId) => api.get(`/vendor-payments/vendor/${vendorId}/outstanding-grns`)
};

// ==================== INVENTORY API ====================
export const productWeightsAPI = {
  list: (params) => api.get('/product-weights', { params }),
  update: (id, data) => api.put(`/product-weights/${id}`, data),
  auto: (id) => api.post(`/product-weights/${id}/auto`),
  autoFill: (data) => api.post('/product-weights/auto-fill', data),
  log: (params) => api.get('/product-weights/log', { params }),
};

export const inventoryAPI = {
  // Stock Views
  getStockOverview: () => api.get('/inventory/stock/overview'),
  getWarehouseStock: (warehouseId, params) => api.get(`/inventory/stock/warehouse/${warehouseId}`, { params }),
  
  // HQ Store Stock Monitoring
  getHQStoresStockOverview: () => api.get('/inventory/hq/stores-stock-overview'),
  getHQStoreStockDetail: (storeId, params) => api.get(`/inventory/hq/store-stock/${storeId}`, { params }),
  
  // Stock Availability Check (for PO approvals)
  checkStockAvailability: (data) => api.post('/inventory/check-stock-availability', data),
  
  // Price Alerts
  getPriceAlerts: (params) => api.get('/inventory/price-alerts', { params }),
  updateSellingPrice: (productId, newPrice, newMrp, reason) => api.post('/inventory/update-selling-price', null, {
    params: { product_id: productId, new_selling_price: newPrice, new_mrp: newMrp, reason }
  }),
  
  // Advanced Pricing
  getProductPricing: (productId) => api.get(`/inventory/products/${productId}/pricing`),
  updateProductPricing: (productId, data) => api.post(`/inventory/products/${productId}/pricing`, null, { params: data }),
  getPriceHistory: (productId, limit) => api.get(`/inventory/price-history/${productId}`, { params: { limit } }),
  
  // Stock Operations
  markDamaged: (stockId, damagedQty, reason) => api.post('/inventory/mark-damaged', null, {
    params: { stock_id: stockId, damaged_qty: damagedQty, reason }
  }),
  stockAdjustment: (data) => api.post('/inventory/stock-adjustment', null, { params: data }),
  getStockAdjustments: (params) => api.get('/inventory/stock-adjustments', { params }),
  
  // Stock History
  getStockMovements: (productId, params) => {
    // If productId is null, use the new general movements endpoint
    if (!productId) {
      return api.get('/inventory/movements', { params });
    }
    return api.get(`/inventory/stock-movements/${productId}`, { params });
  },
  getProductBatches: (productId, warehouseId) => api.get(`/inventory/batches/${productId}`, { params: { warehouse_id: warehouseId } })
};

// ==================== SYNC API ====================
export const syncAPI = {
  listJobs: (params) => api.get('/sync/jobs', { params }),
  getJob: (jobId) => api.get(`/sync/jobs/${jobId}`),
  retryJob: (jobId, locationType, locationIds) => api.post(`/sync/jobs/${jobId}/retry`, { 
    location_type: locationType, 
    location_ids: locationIds 
  }),
  manualSync: (jobId) => api.post(`/sync/jobs/${jobId}/manual-sync`),
  getStats: () => api.get('/sync/stats'),
  bulkProductSync: () => api.post('/sync/bulk-product-sync'),
  bulkPricingSync: () => api.post('/sync/bulk-pricing-sync')
};

// ==================== STOCK TRANSFERS API ====================
export const stockTransfersAPI = {
  list: (params) => api.get('/stock-transfers', { params }),
  get: (id) => api.get(`/stock-transfers/${id}`),
  create: (data) => api.post('/stock-transfers', data),
  update: (id, data) => api.put(`/stock-transfers/${id}`, data),
  delete: (id) => api.delete(`/stock-transfers/${id}`),
  dispatch: (id) => api.post(`/stock-transfers/${id}/dispatch`),
  receive: (id, receivedItems) => api.post(`/stock-transfers/${id}/receive`, receivedItems),
  cancel: (id, reason) => api.post(`/stock-transfers/${id}/cancel`, null, { params: { reason } }),
  getPendingCount: (locationType, locationId) => api.get('/stock-transfers/pending/count', {
    params: { location_type: locationType, location_id: locationId }
  }),
  // New workflow endpoints
  updateStatus: (id, data) => api.post(`/stock-transfers/${id}/update-status`, data),
  reportDiscrepancy: (id, data) => api.post(`/stock-transfers/${id}/report-discrepancy`, data),
  resolveDiscrepancy: (id, notes, acceptReceivedQty) => api.post(`/stock-transfers/${id}/resolve-discrepancy`, null, {
    params: { resolution_notes: notes, accept_received_qty: acceptReceivedQty }
  }),
  getHistory: (id) => api.get(`/stock-transfers/${id}/history`),
  getAlerts: (locationId, locationType) => api.get(`/stock-transfers/alerts/location/${locationId}`, {
    params: { location_type: locationType }
  }),
  getPrintDispatch: (id) => api.get(`/stock-transfers/print-dispatch/${id}`)
};

// ==================== POS API ====================
export const posAPI = {
  stockRepairPending: (params) => api.get('/pos/stock-repair/pending', { params }),
  stockRepairApply: (data) => api.post('/pos/stock-repair/apply', data),
  stockRepairMarkDone: (data) => api.post('/pos/stock-repair/mark-done', data),
  // Cash Register
  openRegister: (data) => api.post('/pos/cash-register/open', data),
  closeRegister: (registerId, data) => api.post(`/pos/cash-register/${registerId}/close`, data),
  getCurrentRegister: (storeId, counterId) => api.get('/pos/cash-register/current', {
    params: { store_id: storeId, counter_id: counterId }
  }),
  getRegisterHistory: (storeId, params) => api.get('/pos/cash-register/history', {
    params: { store_id: storeId, ...params }
  }),
  // Terminal-wise register status (Manager view)
  getTerminalsRegisterStatus: (storeId) => api.get('/pos/cash-register/terminals-status', {
    params: { store_id: storeId }
  }),
  // Cashier's open register check
  getCashierRegisterStatus: (storeId) => api.get('/pos/cash-register/cashier-status', {
    params: { store_id: storeId }
  }),
  
  // Product Lookup
  lookupProduct: (query, storeId) => api.get('/pos/products/lookup', {
    params: { query, store_id: storeId }
  }),
  searchProducts: (search, storeId) => api.get('/pos/products/search', { params: { search, store_id: storeId } }),
  
  // Sales
  createSale: (data) => api.post('/pos/sales', data),
  listSales: (params) => api.get('/pos/sales', { params }),
  salesSummary: (params) => api.get('/pos/sales/summary', { params }),
  voidEmptySale: (id) => api.post(`/pos/sales/${id}/void-empty`),
  getSale: (saleId) => api.get(`/pos/sales/${saleId}`),
  getSaleByBill: (billNumber) => api.get(`/pos/sales/bill/${billNumber}`),
  lookupSale: (query, storeId) => api.get('/pos/sales/lookup', { 
    params: { query, store_id: storeId } 
  }),
  
  // Held Bills
  holdBill: (data) => api.post('/pos/held-bills', data),
  getHeldBills: (storeId, counterId) => api.get('/pos/held-bills', {
    params: { store_id: storeId, counter_id: counterId }
  }),
  getHeldBill: (heldBillId) => api.get(`/pos/held-bills/${heldBillId}`),
  deleteHeldBill: (heldBillId) => api.delete(`/pos/held-bills/${heldBillId}`),
  
  // Returns & Exchanges
  createReturn: (data) => api.post('/pos/returns', data),
  createExchange: (data) => api.post('/pos/exchanges', data),
  quoteExchange: (data) => api.post('/pos/exchanges/quote', data),
  listReturns: (params) => api.get('/pos/returns', { params }),
  listExchanges: (params) => api.get('/pos/exchanges', { params }),
  
  // Dashboard
  getDashboardStats: (storeId) => api.get('/pos/dashboard/stats', { params: { store_id: storeId } })
};

// ==================== CUSTOMER API ====================
export const customerAPI = {
  search: (params) => api.get('/customers/search', { params }),
  getByMobile: (mobile) => api.get(`/customers/by-mobile/${mobile}`),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  list: (params) => api.get('/customers', { params }),
  // Store-specific
  listByStore: (storeId, params) => api.get(`/customers/store/${storeId}/list`, { params }),
  getPurchaseHistory: (id, limit = 20) => api.get(`/customers/${id}/purchase-history`, { params: { limit } }),
  // HQ Analytics
  hqListAll: (params) => api.get('/customers/hq/all', { params }),
  hqGetAnalytics: (id) => api.get(`/customers/hq/${id}/analytics`)
};

// ==================== HQ MONITORING API ====================
export const hqMonitoringAPI = {
  getLiveSales: () => api.get('/hq-monitoring/live-sales'),
  getHourlySales: (storeId) => api.get('/hq-monitoring/hourly-sales', { params: storeId ? { store_id: storeId } : {} }),
  getStoreComparison: (days = 7) => api.get('/hq-monitoring/store-comparison', { params: { days } }),
  getRecentBills: (limit = 20) => api.get('/hq-monitoring/recent-bills', { params: { limit } }),
  // Store Sync Status
  getStoreSyncStatus: () => api.get('/hq-monitoring/store-sync-status'),
  getStoreSyncDetail: (storeId) => api.get(`/hq-monitoring/store-sync-detail/${storeId}`),
  // Store Health Check (Abnormal Activities)
  getStoreHealthCheck: () => api.get('/hq-monitoring/store-health-check')
};

// ==================== BUSINESS SETTINGS API ====================
export const businessSettingsAPI = {
  get: () => api.get('/business-settings'),
  update: (data) => api.put('/business-settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/business-settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: () => api.delete('/business-settings/logo'),
  getPublicBranding: () => api.get('/business-settings/public/branding')
};

// ==================== USER PASSWORD API ====================
export const passwordAPI = {
  changePassword: (currentPassword, newPassword) => api.post('/users/change-password', null, {
    params: { current_password: currentPassword, new_password: newPassword }
  })
};

// ==================== STORE EXPENSES API ====================
export const storeExpensesAPI = {
  list: (params) => api.get('/store-expenses', { params }),
  get: (id) => api.get(`/store-expenses/${id}`),
  create: (data) => api.post('/store-expenses', data),
  update: (id, data) => api.put(`/store-expenses/${id}`, data),
  delete: (id) => api.delete(`/store-expenses/${id}`),
  getCategories: () => api.get('/store-expenses/categories'),
  getSummary: (storeId, year, month) => api.get('/store-expenses/summary', {
    params: { store_id: storeId, year, month }
  }),
  // HQ endpoints
  hqListAll: (params) => api.get('/store-expenses/hq/all-stores', { params }),
  hqGetSummary: (params) => api.get('/store-expenses/hq/summary', { params })
};

// ==================== STORE DAMAGED STOCK API ====================
export const storeDamagedStockAPI = {
  list: (params) => api.get('/store-damaged-stock', { params }),
  get: (id) => api.get(`/store-damaged-stock/${id}`),
  create: (data) => api.post('/store-damaged-stock', data),
  update: (id, data) => api.put(`/store-damaged-stock/${id}`, data),
  delete: (id) => api.delete(`/store-damaged-stock/${id}`),
  getDamageTypes: () => api.get('/store-damaged-stock/damage-types'),
  getSummary: (storeId, year, month) => api.get('/store-damaged-stock/summary', {
    params: { store_id: storeId, year, month }
  }),
  approve: (id) => api.post(`/store-damaged-stock/${id}/approve`),
  reject: (id, reason) => api.post(`/store-damaged-stock/${id}/reject`, null, {
    params: { rejection_reason: reason }
  })
};

// ==================== STORE SYNC API (HQ Monitoring) ====================
export const storeSyncAPI = {
  // Legacy methods
  getStoreInfo: (storeId) => api.get(`/stores/${storeId}/sync-info`),
  getStoreInfoByCode: (storeCode) => api.get(`/stores/by-code/${storeCode}/sync-info`),
  // HQ Monitoring
  listDevices: (storeId, isActive) => api.get('/store-sync/hq/store-devices', {
    params: { store_id: storeId, is_active: isActive }
  }),
  getSyncLogs: (params) => api.get('/store-sync/hq/sync-logs', { params }),
  generateStoreSecret: (storeId) => api.post(`/store-sync/hq/generate-store-secret/${storeId}`)
};

// ==================== STORE TERMINALS API ====================
export const storeTerminalsAPI = {
  list: (storeId, isActive = true) => api.get('/store-terminals', {
    params: { store_id: storeId, is_active: isActive }
  }),
  get: (terminalId) => api.get(`/store-terminals/${terminalId}`),
  create: (data) => api.post('/store-terminals', data),
  update: (terminalId, data) => api.put(`/store-terminals/${terminalId}`, data),
  delete: (terminalId) => api.delete(`/store-terminals/${terminalId}`),
  // Terminal Assignment
  assignUser: (terminalId, userId) => api.post(`/store-terminals/${terminalId}/assign?user_id=${userId}`),
  unassignUser: (terminalId, userId) => api.delete(`/store-terminals/${terminalId}/assign/${userId}`),
  getUserAssignedTerminals: (userId) => api.get(`/store-terminals/user/${userId}/assigned`),
  getStoreStaff: (storeId) => api.get(`/store-terminals/store/${storeId}/staff`)
};

// ==================== MANUFACTURERS API ====================
export const manufacturersAPI = {
  list: (params) => api.get('/manufacturers', { params }),
  dropdown: () => api.get('/manufacturers/dropdown'),
  get: (id) => api.get(`/manufacturers/${id}`),
  create: (data) => api.post('/manufacturers', data),
  update: (id, data) => api.put(`/manufacturers/${id}`, data),
  delete: (id) => api.delete(`/manufacturers/${id}`),
  getCountries: () => api.get('/manufacturers/meta/countries'),
  getStates: (countryCode) => api.get(`/manufacturers/meta/states/${countryCode}`)
};

// ==================== REVENUE MANAGEMENT API ====================
export const revenueAPI = {
  // HQ Settings
  getHQSettings: () => api.get('/revenue/hq-settings'),
  updateHQSettings: (data) => api.put('/revenue/hq-settings', data),
  
  // Store Rack Settings
  getStoreRackSettings: (storeId) => api.get(`/revenue/stores/${storeId}/rack-settings`),
  updateStoreRackSettings: (storeId, data) => api.put(`/revenue/stores/${storeId}/rack-settings`, data),
  
  // Store Revenue Settings
  getStoreRevenueSettings: (storeId) => api.get(`/revenue/stores/${storeId}/revenue-settings`),
  updateStoreRevenueSettings: (storeId, data) => api.put(`/revenue/stores/${storeId}/revenue-settings`, data),
  
  // Subscriptions
  listSubscriptions: (params) => api.get('/revenue/subscriptions', { params }),
  createSubscription: (data) => api.post('/revenue/subscriptions', data),
  updateSubscription: (id, data) => api.put(`/revenue/subscriptions/${id}`, data),
  
  // Invoices
  listInvoices: (params) => api.get('/revenue/invoices', { params }),
  getInvoice: (id) => api.get(`/revenue/invoices/${id}`),
  createInvoice: (data) => api.post('/revenue/invoices', data),
  updateInvoice: (id, data) => api.put(`/revenue/invoices/${id}`, data),
  cancelInvoice: (id, data) => api.post(`/revenue/invoices/${id}/cancel`, data),
  generateMonthlyInvoices: (billingPeriod) => api.post(`/revenue/invoices/generate-monthly?billing_period=${billingPeriod}`),
  
  // Payments
  listPayments: (params) => api.get('/revenue/payments', { params }),
  createPayment: (data) => api.post('/revenue/payments', data)
};

// Logistics API
export const logisticsAPI = {
  list: (params) => api.get('/logistics/partners', { params }).then(res => res.data),
  get: (id) => api.get(`/logistics/partners/${id}`).then(res => res.data),
  create: (data) => api.post('/logistics/partners', data).then(res => res.data),
  update: (id, data) => api.put(`/logistics/partners/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/logistics/partners/${id}`).then(res => res.data),
  activate: (id) => api.post(`/logistics/partners/${id}/activate`).then(res => res.data),
  getDropdown: () => api.get('/logistics/partners/dropdown').then(res => res.data),
  getServiceTypes: () => api.get('/logistics/service-types').then(res => res.data),
  getRateTypes: () => api.get('/logistics/rate-types').then(res => res.data)
};

// Expenses API
export const expensesAPI = {
  // Expense Heads (Categories)
  listHeads: (params) => api.get('/expenses/heads', { params }).then(res => res.data),
  getHeadsDropdown: (scope) => api.get('/expenses/heads/dropdown', { params: { scope } }).then(res => res.data),
  getHead: (id) => api.get(`/expenses/heads/${id}`).then(res => res.data),
  createHead: (data) => api.post('/expenses/heads', data).then(res => res.data),
  updateHead: (id, data) => api.put(`/expenses/heads/${id}`, data).then(res => res.data),
  deleteHead: (id) => api.delete(`/expenses/heads/${id}`).then(res => res.data),
  seedDefaultHeads: () => api.post('/expenses/heads/seed-defaults').then(res => res.data),
  
  // Expenses
  list: (params) => api.get('/expenses', { params }).then(res => res.data),
  get: (id) => api.get(`/expenses/${id}`).then(res => res.data),
  create: (data) => api.post('/expenses', data).then(res => res.data),
  update: (id, data) => api.put(`/expenses/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/expenses/${id}`).then(res => res.data),
  
  // Unified Expenses (HQ view - includes HQ, Stores, Warehouses)
  listUnified: (params) => api.get('/expenses/all/unified', { params }).then(res => res.data),
  getUnifiedSummary: (params) => api.get('/expenses/all/summary', { params }).then(res => res.data),
  
  // Reports
  getSummary: (params) => api.get('/expenses/summary', { params }).then(res => res.data),
  getByEntity: (params) => api.get('/expenses/by-entity', { params }).then(res => res.data),
  
  // Dropdowns
  getPaymentModes: () => api.get('/expenses/payment-modes/list').then(res => res.data),
  getRecurrenceTypes: () => api.get('/expenses/recurrence-types/list').then(res => res.data),
  getScopes: () => api.get('/expenses/scopes/list').then(res => res.data)
};

// E-Commerce API
export const ecommerceAPI = {
  // Store E-commerce Settings
  listStoreSettings: () => api.get('/ecommerce/store-settings').then(res => res.data),
  getStoreSettings: (storeId) => api.get(`/ecommerce/store-settings/${storeId}`).then(res => res.data),
  updateStoreSettings: (storeId, data) => api.put(`/ecommerce/store-settings/${storeId}`, data).then(res => res.data),
  
  // Own Logistics Pincodes
  addOwnLogisticsPincodes: (storeId, pincodes) => api.post(`/ecommerce/store-settings/${storeId}/own-logistics-pincodes`, pincodes).then(res => res.data),
  removeOwnLogisticsPincode: (storeId, pincode) => api.delete(`/ecommerce/store-settings/${storeId}/own-logistics-pincodes/${pincode}`).then(res => res.data),
  
  // Free Shipping Pincodes
  addFreeShippingPincodes: (storeId, pincodes) => api.post(`/ecommerce/store-settings/${storeId}/free-shipping-pincodes`, pincodes).then(res => res.data),
  removeFreeShippingPincode: (storeId, pincode) => api.delete(`/ecommerce/store-settings/${storeId}/free-shipping-pincodes/${pincode}`).then(res => res.data),
  
  // Delivery Slots
  updateDeliverySlots: (storeId, slots) => api.put(`/ecommerce/store-settings/${storeId}/delivery-slots`, slots).then(res => res.data),
  
  // Public APIs
  checkPincode: (pincode) => api.get(`/ecommerce/check-pincode/${pincode}`).then(res => res.data),
  calculateShipping: (pincode, weight, cartValue) => api.post('/ecommerce/calculate-shipping', null, { 
    params: { pincode, total_weight_kg: weight, cart_value: cartValue } 
  }).then(res => res.data)
};

// ==================== ASSETS API ====================
export const assetsAPI = {
  getSettings: () => api.get('/assets/settings'),
  updateSettings: (data) => api.put('/assets/settings', data),
  testConnection: () => api.post('/assets/test-connection'),
  uploadProductImage: (productId, file, isPrimary = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_primary', isPrimary);
    return api.post(`/assets/upload/product/${productId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteProductImage: (productId, imageUrl) => api.delete(`/assets/product/${productId}/image`, {
    params: { image_url: imageUrl }
  }),
  getProductImages: (productId) => api.get(`/assets/product/${productId}`)
};

// ==================== TRANSFERS API ====================
export const transfersAPI = {
  // Pricing Levels
  listPricingLevels: (params) => api.get('/transfers/pricing-levels', { params }),
  createPricingLevel: (data) => api.post('/transfers/pricing-levels', data),
  updatePricingLevel: (id, data) => api.put(`/transfers/pricing-levels/${id}`, data),
  deletePricingLevel: (id) => api.delete(`/transfers/pricing-levels/${id}`),
  
  // Franchise Credit Status
  getFranchiseCreditStatus: (storeId) => api.get(`/transfers/franchise/${storeId}/credit-status`),
  
  // Stock Requests
  listRequests: (params) => api.get('/transfers/requests', { params }),
  getRequest: (id) => api.get(`/transfers/requests/${id}`),
  createRequest: (data) => api.post('/transfers/requests', data),
  appendItems: (id, items) => api.post(`/transfers/requests/${id}/items`, { items }),
  updateRequest: (id, data) => api.put(`/transfers/requests/${id}`, data),
  submitRequest: (id) => api.post(`/transfers/requests/${id}/submit`),
  approveRequest: (id, data) => api.post(`/transfers/requests/${id}/approve`, data),
  rejectRequest: (id, notes) => api.post(`/transfers/requests/${id}/reject`, null, { params: { review_notes: notes } }),
  
  // Transfer Orders
  listOrders: (params) => api.get('/transfers/orders', { params }),
  getOrder: (id) => api.get(`/transfers/orders/${id}`),
  createOrder: (data) => api.post('/transfers/orders', data),
  processOrder: (id) => api.post(`/transfers/orders/${id}/process`),
  dispatchOrder: (id, data) => api.post(`/transfers/orders/${id}/dispatch`, data),
  receiveOrder: (id, data) => api.post(`/transfers/orders/${id}/receive`, data),
  reconcileStock: (id) => api.post(`/transfers/orders/${id}/reconcile-stock`),
  listInTransit: () => api.get('/transfers/orders/in-transit/pending'),
  cancelInTransit: (id, reason) => api.post(`/transfers/orders/${id}/cancel-in-transit`, { reason }),
  getOrderTimeline: (id) => api.get(`/transfers/orders/${id}/timeline`)
};

// ==================== LOW STOCK ALERTS API ====================
export const lowStockAlertsAPI = {
  // Store Settings
  getStoreSettings: (storeId, params) => api.get(`/low-stock-alerts/store/${storeId}/settings`, { params }),
  updateStoreSetting: (storeId, productId, data) => api.put(`/low-stock-alerts/store/${storeId}/settings/${productId}`, data),
  bulkUpdateStoreSettings: (storeId, data) => api.post(`/low-stock-alerts/store/${storeId}/settings/bulk`, data),
  deleteStoreSetting: (storeId, productId) => api.delete(`/low-stock-alerts/store/${storeId}/settings/${productId}`),
  
  // Alerts
  getAlerts: (params) => api.get('/low-stock-alerts/alerts', { params }),
  acknowledgeAlert: (productId, locationId, data) => api.post(`/low-stock-alerts/alerts/${productId}/${locationId}/acknowledge`, data),
  removeAcknowledgement: (productId, locationId) => api.delete(`/low-stock-alerts/alerts/${productId}/${locationId}/acknowledge`),
  
  // Dashboard
  getDashboardSummary: () => api.get('/low-stock-alerts/dashboard/summary')
};

// ==================== STORE PRICING API ====================
export const storePricingAPI = {
  // Store Product Prices
  getStoreProducts: (storeId, params) => api.get(`/store-pricing/store/${storeId}/products`, { params }),
  updateProductPrice: (storeId, productId, data) => api.put(`/store-pricing/store/${storeId}/products/${productId}`, data),
  bulkUpdatePrices: (storeId, data) => api.post(`/store-pricing/store/${storeId}/products/bulk-update`, data),
  resetToHQPrices: (storeId, data) => api.post(`/store-pricing/store/${storeId}/products/reset-to-hq`, data),
  deleteCustomPrice: (storeId, productId) => api.delete(`/store-pricing/store/${storeId}/products/${productId}`),
  
  // HQ View
  hqGetStorePrices: (storeId, params) => api.get(`/store-pricing/hq/stores/${storeId}/prices`, { params }),
  hqSyncPrices: (data) => api.post('/store-pricing/hq/sync-hq-prices', data)
};

// ==================== LEDGERS API ====================
export const ledgersAPI = {
  // Franchise Ledger
  listFranchiseLedgers: (params) => api.get('/ledgers/franchise', { params }),
  getFranchiseLedger: (storeId) => api.get(`/ledgers/franchise/${storeId}`),
  getFranchiseLedgerEntries: (storeId, params) => api.get(`/ledgers/franchise/${storeId}/entries`, { params }),
  initializeFranchiseLedger: (storeId, data) => api.post(`/ledgers/franchise/${storeId}/initialize`, null, { params: data }),
  createFranchisePayment: (data) => api.post('/ledgers/franchise/payments', data),
  listFranchisePayments: (params) => api.get('/ledgers/franchise/payments', { params }),
  
  // Vendor Ledger
  listVendorLedgers: (params) => api.get('/ledgers/vendor', { params }),
  getVendorLedger: (vendorId) => api.get(`/ledgers/vendor/${vendorId}`),
  getVendorLedgerEntries: (vendorId, params) => api.get(`/ledgers/vendor/${vendorId}/entries`, { params }),
  initializeVendorLedger: (vendorId, data) => api.post(`/ledgers/vendor/${vendorId}/initialize`, null, { params: data }),
  createVendorPayment: (data) => api.post('/ledgers/vendor/payments', data),
  listVendorPayments: (params) => api.get('/ledgers/vendor/payments', { params }),
  vendorPaymentReminders: (params) => api.get('/ledgers/vendor/payment-reminders', { params }),
  reconcileVendorLedgers: (vendorId) => api.post('/ledgers/vendor/reconcile', null, { params: vendorId ? { vendor_id: vendorId } : {} }),
  
  // Adjustment Notes
  listAdjustmentNotes: (params) => api.get('/ledgers/adjustment-notes', { params }),
  createAdjustmentNote: (data) => api.post('/ledgers/adjustment-notes', data),
  approveAdjustmentNote: (id) => api.post(`/ledgers/adjustment-notes/${id}/approve`),
  getAdjustmentNote: (id) => api.get(`/ledgers/adjustment-notes/${id}`),
  updateAdjustmentNote: (id, data) => api.put(`/ledgers/adjustment-notes/${id}`, data),
  adjustmentNotePDF: (id) => `/ledgers/adjustment-notes/${id}/pdf`
};

// ==================== LOCAL VENDORS API ====================
export const localVendorsAPI = {
  // CRUD
  list: (params) => api.get('/local-vendors', { params }),
  get: (id) => api.get(`/local-vendors/${id}`),
  create: (data) => api.post('/local-vendors', data),
  update: (id, data) => api.put(`/local-vendors/${id}`, data),
  delete: (id) => api.delete(`/local-vendors/${id}`),
  
  // Products
  getProducts: (id, params) => api.get(`/local-vendors/${id}/products`, { params }),
  
  // Payments
  listPayments: (vendorId, params) => api.get(`/local-vendors/${vendorId}/payments`, { params }),
  createPayment: (vendorId, data) => api.post(`/local-vendors/${vendorId}/payments`, data),
  
  // Ledger
  getLedger: (vendorId, params) => api.get(`/local-vendors/${vendorId}/ledger`, { params }),
  
  // HQ Monitoring
  hqListAll: (params) => api.get('/local-vendors/hq/all-vendors', { params }),
  hqGetStorePurchases: (storeId, params) => api.get(`/local-vendors/hq/store/${storeId}/purchases`, { params })
};

// ==================== STORE PURCHASE ORDERS API ====================
export const storePurchaseOrdersAPI = {
  // CRUD
  list: (params) => api.get('/store-purchase-orders', { params }),
  get: (id) => api.get(`/store-purchase-orders/${id}`),
  create: (data) => api.post('/store-purchase-orders', data),
  update: (id, data) => api.put(`/store-purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/store-purchase-orders/${id}`),
  
  // Actions
  submit: (id) => api.post(`/store-purchase-orders/${id}/submit`),
  cancel: (id, reason) => api.post(`/store-purchase-orders/${id}/cancel`, null, { params: { reason } }),
  receive: (id, data) => api.post(`/store-purchase-orders/${id}/receive`, data),
  
  // HQ Actions
  hqPendingApprovals: (params) => api.get('/store-purchase-orders/hq/pending-approvals', { params }),
  hqApprove: (id, data) => api.post(`/store-purchase-orders/${id}/hq-approve`, data),
  hqReject: (id, reason) => api.post(`/store-purchase-orders/${id}/hq-reject`, null, { params: { reason } }),
  
  // HQ Franchise Direct PO Monitoring
  hqFranchiseDirectList: (params) => api.get('/store-purchase-orders/hq/franchise-direct', { params }),
  hqFranchiseDirectStats: () => api.get('/store-purchase-orders/hq/franchise-direct/stats'),
  
  // Stats
  getStats: () => api.get('/store-purchase-orders/store/stats')
};

// Store GRN API (Multi-source GRN for stores)
export const storeGRNAPI = {
  // List GRNs for current store
  list: (params) => api.get('/store/grn', { params }),
  get: (id) => api.get(`/store/grn/${id}`),
  create: (data) => api.post('/store/grn', data),
  
  // Pending items for GRN
  getPendingTransfers: () => api.get('/store/grn/pending/transfers'),
  getPendingCentralVendorPOs: () => api.get('/store/grn/pending/central-vendor-pos'),
  getPendingLocalVendorPOs: () => api.get('/store/grn/pending/local-vendor-pos'),
  getPendingFranchiseInvoices: () => api.get('/store/grn/pending/franchise-invoices'),
  
  // Local vendors for direct entry
  getLocalVendors: () => api.get('/store/grn/local-vendors')
};

// Store Vendor Ledger API (for franchise stores - includes local + central vendors)
export const storeVendorLedgerAPI = {
  // Ledger endpoints
  list: (params) => api.get('/store/vendor-ledger', { params }),
  getSummary: () => api.get('/store/vendor-ledger/summary'),
  get: (vendorId) => api.get(`/store/vendor-ledger/${vendorId}`),
  getEntries: (vendorId, params) => api.get(`/store/vendor-ledger/${vendorId}/entries`, { params }),
  getPendingInvoices: (vendorId) => api.get(`/store/vendor-ledger/${vendorId}/pending-invoices`),
  
  // Payment endpoints
  listPayments: (params) => api.get('/store/vendor-ledger/payments/list', { params }),
  createPayment: (data) => api.post('/store/vendor-ledger/payments', data),
  
  // HQ monitoring
  hqMonitorLedgers: (params) => api.get('/store/vendor-ledger/hq/monitor', { params }),
  hqMonitorPayments: (params) => api.get('/store/vendor-ledger/hq/monitor/payments', { params }),
  hqMonitorStats: () => api.get('/store/vendor-ledger/hq/monitor/stats')
};

// ==================== STOCK REQUESTS API ====================
export const stockRequestsAPI = {
  list: (params) => api.get('/stock-requests', { params }).then(res => res.data),
  get: (id) => api.get(`/stock-requests/${id}`).then(res => res.data),
  create: (data) => api.post('/stock-requests', data).then(res => res.data),
  submit: (id) => api.post(`/stock-requests/${id}/submit`).then(res => res.data),
  approve: (id, data) => api.post(`/stock-requests/${id}/approve`, data).then(res => res.data),
  reject: (id, reason) => api.post(`/stock-requests/${id}/reject`, null, { params: { reason } }).then(res => res.data),
  delete: (id) => api.delete(`/stock-requests/${id}`).then(res => res.data),
  getPendingCount: () => api.get('/stock-requests/dashboard/pending-count').then(res => res.data),
  listPendingDispatch: () => api.get('/stock-requests/pending-dispatch').then(res => res.data)
};

// ==================== SECURITY / MARGIN GUARD API ====================
export const securityAPI = {
  getCode: () => api.get('/security/code'),
  regenerate: () => api.post('/security/code/regenerate'),
  codeUsages: (limit = 50) => api.get('/security/code/usages', { params: { limit } }),
  validate: (data) => api.post('/security/code/validate', data),
  marginLogs: (params) => api.get('/security/margin-logs', { params }),
  marginAlerts: (limit = 5) => api.get('/security/margin-alerts', { params: { limit } }),
  acknowledgeMarginLog: (id) => api.post(`/security/margin-logs/${id}/acknowledge`),
  acknowledgeAllMarginLogs: () => api.post('/security/margin-logs/acknowledge-all')
};

export default api;

